import { Badge } from "@solomon-apps/shared/src/components/ui/badge";
import { ConfirmModal } from "@solomon-apps/shared/src/components/ui/confirm-modal";
import { FormActions } from "@solomon-apps/shared/src/components/ui/form-actions";
import { Heading } from "@solomon-apps/shared/src/components/ui/heading";
import { Text } from "@solomon-apps/shared/src/components/ui/text";
import React, { useCallback, useState } from "react";

import { resolveClientFormError } from "@/utils/clientFormErrors";

import { programsByIds, programUserCount } from "../shared/programMocks";

import { ConstraintSummary } from "./ConstraintSummary";
import {
    formatPolicyValue,
    PERMISSION_LABELS,
    type PolicyDefinition
} from "./policiesMocks";
import { type PolicyEditorSummary, resolvePolicyEditor } from "./policyEditors";

type SaveState = "idle" | "saving" | "saved" | "error";

/** The empty summary used before the mounted editor reports its first one. */
const EMPTY_SUMMARY: PolicyEditorSummary = {
    serialized: "",
    dirty: false,
    valid: false,
    minViolated: false,
    maxViolated: false,
    changeLines: null,
    errorLine: null,
    reset: () => undefined
};

export interface PolicyConfigEditorProps {
    /** The policy instance being configured. */
    policy: PolicyDefinition;
    /**
     * Persists the edited value. Receives the policy and the serialized value
     * string. May throw when the server cannot persist the change.
     */
    onSave: (policy: PolicyDefinition, serialized: string) => Promise<void>;
}

/**
 * `PolicyConfigEditor` — the editable configuration for a single policy.
 *
 * Owns the edit-state lifecycle (via the per-type editor it mounts), the
 * save-state machine, and the four-slot layout: Heading → Input →
 * ConstraintSummary → FormActions, plus the save ConfirmModal.
 *
 * The Input slot is chosen from a per-type registry (`policyEditors.tsx`):
 * each entry is a self-contained editor that seeds its own state from the
 * policy, renders its input, validates, serializes, and reports a uniform
 * `PolicyEditorSummary` up. This shell stays type-agnostic — adding a new
 * policy type is one registry entry, and this file never changes.
 *
 * Locked, non-wallet policies short-circuit to a read-only view (value +
 * Locked badge, no input/CTAs). Locked wallet-list policies still render the
 * editor (disabled), with CTAs gated by dirtiness.
 */
export function PolicyConfigEditor({
    policy,
    onSave
}: PolicyConfigEditorProps): React.ReactElement {
    const constraint = policy.constraint;
    const locked = policy.permission === "locked";
    const isWalletList = constraint.kind === "options";
    const isTranche = policy.category === "tranche";

    const [summary, setSummary] = useState<PolicyEditorSummary>(EMPTY_SUMMARY);
    const [saveState, setSaveState] = useState<SaveState>("idle");
    const [saveErr, setSaveErr] = useState<string | null>(null);
    const [confirmOpen, setConfirmOpen] = useState(false);
    // Bumped to ask the mounted editor to reset (the Discard action).
    const [resetSignal, setResetSignal] = useState(0);

    // Reset the save-state machine whenever the editor reports a change. The
    // clean re-report that follows a save (the seed catches up with the
    // draft) keeps "saved", so the confirmation stays up.
    const handleSummaryChange = useCallback(
        (next: PolicyEditorSummary): void => {
            setSummary(next);
            setSaveState((prev) =>
                prev === "saved" && !next.dirty ? prev : "idle"
            );
            setSaveErr(null);
        },
        []
    );

    // Blast radius — programs this policy is attached to + their enrolled
    // user counts, surfaced in the save-confirmation modal.
    const affectedPrograms = programsByIds(policy.programIds).map((p) => ({
        name: p.name,
        users: programUserCount(p.id)
    }));
    const totalUsers = affectedPrograms.reduce((sum, p) => sum + p.users, 0);

    const handleSave = async (): Promise<void> => {
        setSaveState("saving");
        setSaveErr(null);
        try {
            await onSave(policy, summary.serialized);
            setSaveState("saved");
        } catch (e) {
            setSaveState("error");
            setSaveErr(
                resolveClientFormError(
                    e,
                    "policy",
                    "Policy was not saved. Check the value and distributor constraints, then try again."
                )
            );
        }
    };

    // Non-wallet locked policies stay read-only (value + badge). The locked
    // Whitelist/Blacklist instead renders the editor disabled (handled below).
    if (locked && !isWalletList) {
        return (
            <div className="mt-5 max-w-xl flex flex-col gap-4">
                <Heading title={policy.value} titleVariant="h3" divider />
                <Badge variant="quaternary" className="self-start mt-4">
                    {PERMISSION_LABELS[policy.permission]}
                </Badge>
            </div>
        );
    }

    const entry = resolvePolicyEditor(policy);
    const Editor = entry.Editor;

    const primaryDisabled =
        !(summary.dirty && summary.valid) || saveState === "saving";
    const cancelDisabled = !summary.dirty || saveState === "saving";

    return (
        <>
            <Editor
                key={`${policy.id}:${entry.id}`}
                policy={policy}
                onSummaryChange={handleSummaryChange}
                resetSignal={resetSignal}
                locked={locked}
            />

            {/* Issuer constraints — permission + bound, on one line. */}
            <ConstraintSummary
                permission={policy.permission}
                constraint={constraint}
                minViolated={summary.minViolated}
                maxViolated={summary.maxViolated}
                className={isTranche || isWalletList ? "mt-4" : "mt-2"}
            />

            {summary.errorLine ? (
                <Text variant="fine-print" className="text-error mt-3">
                    {summary.errorLine}
                </Text>
            ) : null}

            <div className="flex items-center gap-3 mt-12">
                <FormActions
                    primaryLabel={saveState === "saving" ? "Saving…" : "Save"}
                    onPrimary={() => setConfirmOpen(true)}
                    primaryDisabled={primaryDisabled}
                    cancelLabel="Discard"
                    onCancel={() => {
                        summary.reset();
                        setResetSignal((n) => n + 1);
                    }}
                    cancelDisabled={cancelDisabled}
                />
                {saveState === "saved" && !summary.dirty ? (
                    <Text variant="fine-print" className="text-success-base">
                        Saved
                    </Text>
                ) : null}
                {saveState === "error" && saveErr ? (
                    <Text variant="fine-print" className="text-error">
                        {saveErr}
                    </Text>
                ) : null}
            </div>

            <ConfirmModal
                open={confirmOpen}
                onOpenChange={setConfirmOpen}
                titleVariant="h2"
                title="Apply policy change?"
                description="This updates the value for every program this policy is attached to."
                confirmLabel="Apply change"
                onConfirm={() => {
                    setConfirmOpen(false);
                    void handleSave();
                }}
            >
                <div className="flex flex-col gap-4">
                    <div className="flex flex-col gap-1">
                        <Text
                            variant="small"
                            render={<span />}
                            className="text-foreground-muted"
                        >
                            Change
                        </Text>
                        {summary.changeLines !== null ? (
                            <div className="flex flex-col gap-1">
                                {summary.changeLines.map((line, i) => (
                                    <Text
                                        key={i}
                                        variant="body"
                                        render={<span />}
                                        className="text-foreground-base"
                                    >
                                        {line}
                                    </Text>
                                ))}
                            </div>
                        ) : (
                            <Text
                                variant="body"
                                render={<span />}
                                className="text-foreground-base"
                            >
                                {policy.value} →{" "}
                                {formatPolicyValue(
                                    constraint,
                                    summary.serialized
                                )}
                            </Text>
                        )}
                    </div>
                    {affectedPrograms.length > 0 ? (
                        <div className="flex flex-col gap-2">
                            <Text
                                variant="small"
                                render={<span />}
                                className="text-foreground-muted"
                            >
                                Affects {totalUsers.toLocaleString()} users
                                across {affectedPrograms.length}{" "}
                                {affectedPrograms.length === 1
                                    ? "program"
                                    : "programs"}
                            </Text>
                            <div className="border-y border-line-base divide-y divide-line-base">
                                {affectedPrograms.map((p) => (
                                    <div
                                        key={p.name}
                                        className="flex items-center justify-between py-2"
                                    >
                                        <Text
                                            variant="small"
                                            render={<span />}
                                            className="text-foreground-base"
                                        >
                                            {p.name}
                                        </Text>
                                        <Text
                                            variant="small"
                                            render={<span />}
                                            className="text-foreground-muted"
                                        >
                                            {p.users.toLocaleString()} users
                                        </Text>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ) : null}
                </div>
            </ConfirmModal>
        </>
    );
}
